import { useRef, useState } from 'react';

/**
 * Attaching a photograph — RX-MED-04.
 *
 * The strip photo is the thing that makes DrBell worth using: a name means
 * little to somebody on eight medications, and a picture of the actual strip
 * in their drawer means everything. The API to store one has existed from the
 * beginning, and until now nothing in any app could produce one — so the
 * feature was unreachable, and the marketing site was describing it anyway.
 *
 * `capture` is deliberately not set. On a phone it would open the camera
 * directly, which is right for a nurse holding the box; on the office
 * computer where most of these are actually added it would be wrong, and a
 * scanned prescription already in Downloads is the common case. Leaving it
 * off lets the device offer both.
 */
export interface PhotoUploader {
  /** POSTs the bytes and returns the stored key and a URL to display. */
  upload: (file: File) => Promise<{ key: string; url: string }>;
}

/** What the API accepts. Stated here so the picker filters, not just refuses. */
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_BYTES = 12 * 1024 * 1024;

export function PhotoField({
  label, hint, value, uploader, onChange, disabled,
}: {
  label: string;
  hint?: string;
  /** `{key, url}` once attached. */
  value: { key: string; url: string } | null;
  uploader: PhotoUploader;
  onChange: (next: { key: string; url: string } | null) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);

    /*
     * Checked here as well as on the server.
     *
     * Not because the client is trusted — it is not, and the server checks
     * the magic bytes — but because uploading 40MB over a ward's broadband
     * to be told no is a minute of somebody's life for a refusal that was
     * knowable instantly.
     */
    if (file.size > MAX_BYTES) {
      setError(`That photo is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is 12MB — most phone cameras produce 2–5MB.`);
      return;
    }

    setBusy(true);
    try {
      onChange(await uploader.upload(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      // Let the same file be chosen again after a failure.
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div>
      <span className="text-xs text-(--muted)">{label}</span>

      {value ? (
        <div className="mt-1 flex items-start gap-3 rounded-lg border border-(--line) bg-(--panel) p-2">
          <img
            src={value.url}
            alt={label}
            className="h-20 w-20 rounded-lg object-cover"
          />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => input.current?.click()}
              className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent) disabled:opacity-40"
            >
              Replace
            </button>
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => onChange(null)}
              className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500 disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => input.current?.click()}
          className="mt-1 flex min-h-11 w-full items-center justify-center rounded-lg border border-dashed border-(--line) px-3 text-sm text-(--muted) hover:border-(--accent) disabled:opacity-40"
        >
          {busy ? 'Uploading…' : '+ Add a photo'}
        </button>
      )}

      {hint && <span className="mt-1 block text-xs text-(--muted)">{hint}</span>}

      {error && (
        <p role="alert" className="mt-1 rounded-lg border border-red-500/30 bg-red-500/10 px-2 py-1 text-xs text-red-500">
          {error}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}
